import { markdownToHTML, Post } from "@/lib/blog";
import { sanitizeArticleHtml } from "@/lib/sanitize";
import { siteConfig } from "@/lib/config";
import path from "path";

// WordPress Post Interface
interface WordPressPost {
  id: number;
  date: string;
  modified: string;
  slug: string;
  title: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  featured_media: number;
  meta?: {
    rank_math_title?: string;
    rank_math_description?: string;
    rank_math_canonical_url?: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
      alt_text?: string;
      media_details?: {
        width: number;
        height: number;
      };
    }>;
    author?: Array<{
      name?: string
      _links?: {
        self?: any
      }
    }>
  };
  translations?: {
    de?: number;
    en?: number;
  };
}

// WordPress API URL direkt im Code definiert (nicht über Umgebungsvariablen)
const WORDPRESS_API_URL = 'https://savagescorpai.kinsta.cloud/wp-json/wp/v2';

// Hilfsfunktion, um die API-URL korrekt zu erstellen
function getWordPressUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${WORDPRESS_API_URL}${normalizedPath}`;
}

// Funktion zum Extrahieren des Textes aus HTML
function stripHtml(html: string): string {
  // Entferne HTML-Tags und ersetze häufige HTML-Entitäten
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#8217;/g, "'")
    .replace(/&#8216;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&#8211;/g, '-')
    .replace(/&#8212;/g, '—')
    .replace(/&hellip;/g, '...')
    .replace(/&#\d+;/g, '') // Entferne alle anderen numerischen HTML-Entitäten
    .trim();
}

// Funktion zum Kürzen von Text auf eine bestimmte Länge
function truncateText(text: string, maxLength: number = 150): string {
  if (text.length <= maxLength) {
    return text;
  }
  
  // Kürze den Text und füge "..." hinzu
  return text.substring(0, maxLength).trim() + '...';
}

// Funktion zum Generieren von IDs aus Text für Überschriften
function generateId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Entferne Sonderzeichen
    .replace(/\s+/g, '-')     // Ersetze Leerzeichen durch Bindestriche
    .replace(/--+/g, '-')     // Ersetze mehrere Bindestriche durch einen
    .trim();
}

// Funktion zum Extrahieren von Überschriften aus HTML-Inhalt
function extractHeadings(html: string): Array<{ text: string; level: number; id: string }> {
  const headings: Array<{ text: string; level: number; id: string }> = [];
  const headingRegex = /<h([2-6]).*?>(.*?)<\/h\1>/g;
  
  let match;
  while ((match = headingRegex.exec(html)) !== null) {
    const level = parseInt(match[1], 10);
    const text = stripHtml(match[2]);
    const id = generateId(text);
    
    headings.push({ text, level, id });
  }
  
  return headings;
}

// Funktion zum Hinzufügen von IDs zu Überschriften im HTML
function addIdsToHeadings(html: string, headings: Array<{ text: string; level: number; id: string }>): string {
  let modifiedHtml = html;
  
  headings.forEach(heading => {
    const headingRegex = new RegExp(`<h${heading.level}>(${heading.text})<\/h${heading.level}>`, 'g');
    modifiedHtml = modifiedHtml.replace(
      headingRegex, 
      `<h${heading.level} id="${heading.id}">${heading.text}</h${heading.level}>`
    );
  });
  
  return modifiedHtml;
}
export async function getBlogPostsPageFromWordPress(locale: string = 'en', items: number, pages: number): Promise<any> {
  try {
    const wordpressUrl = getWordPressUrl(`posts?_embed&per_page=${items}&page=${pages}&lang=${locale}`);
    const response = await fetch(wordpressUrl);
    
    if (!response.ok) {
      return [];
    }
    
    const posts: WordPressPost[] = await response.json();

    if (!Array.isArray(posts)) {
      return [];
    }
    return response.headers.get('X-WP-TotalPages')
  } catch (error) {
    return [];
  }
}
export async function getBlogPostsFromWordPress(locale: string = 'en', items: number, pages: number): Promise<Post[]> {
  try {
    // Rufe Posts mit eingebetteten Medien und Metadaten ab und filtere nach Sprache
    const wordpressUrl = getWordPressUrl(`posts?_embed&per_page=${items}&page=${pages}&lang=${locale}`);
    
    // API-Anfrage an WordPress
    
    const response = await fetch(wordpressUrl);
    
    if (!response.ok) {
      return [];
    }
    
    const posts: WordPressPost[] = await response.json();
    
    
    // Prüfe, ob posts ein Array ist
    if (!Array.isArray(posts)) {
      return [];
    }
      
    return posts.map((post: WordPressPost) => {
      // Bestimme den Bildpfad
      let coverImagePath = null;
      
      // Verwende den lokalen Pfad zum heruntergeladenen Bild in public
      if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0]) {
        const sourceUrl = post._embedded['wp:featuredmedia'][0].source_url;
        // Muss safeExtension() in scripts/download-images.js entsprechen: dort
        // wird die Endung kleingeschrieben abgelegt, ein ".PNG" wuerde hier
        // sonst auf ein nicht existierendes cover.PNG zeigen (Linux = case-sensitive).
        const fileExt = safeImageExtension(sourceUrl);
        // Verwende den Pfad für Next.js Image-Komponente
        coverImagePath = `/blog-images/${post.slug}/cover${fileExt}`;
      }
        
      // Verwende die Rank Math Beschreibung, wenn verfügbar, sonst den Excerpt
      let summary = post.meta?.rank_math_description 
        ? post.meta.rank_math_description 
        : stripHtml(post.excerpt.rendered);
      
      // Kürze die Zusammenfassung auf 150 Zeichen
      summary = truncateText(summary, 150);
        
      return {
        title: post.title.rendered,
        publishedAt: post.date,
        summary: summary,
        author: "SavagesCorp", // Standardwert, kann später angepasst werden
        slug: post.slug,
        image: coverImagePath || `${siteConfig.url}/og?title=${encodeURIComponent(post.title.rendered)}`,
        translations: post.translations
      };
    });
  } catch (error) {
    return [];
  }
}

export async function getPostFromWordPress(slug: string, locale: string = 'en') {
  try {
    // Artikel mit dem Slug abrufen und nach Sprache filtern
    const wordpressUrl = getWordPressUrl(`posts?slug=${slug}&_embed&lang=${locale}`);
    
    // API-Anfrage an WordPress
    
    const response = await fetch(wordpressUrl);
    
    if (!response.ok) {
      return null;
    }
    
    const posts: WordPressPost[] = await response.json();
    
    // Prüfe, ob posts ein Array ist und mindestens ein Element enthält
    if (!Array.isArray(posts) || posts.length === 0) {
      return null;
    }
      
    const post = posts[0];
    
    // Extrahiere Überschriften aus dem HTML-Inhalt
    const headings = extractHeadings(post.content.rendered);
    
    // Füge IDs zu den Überschriften im HTML hinzu und ersetze Bild-URLs
    let contentWithIds = addIdsToHeadings(post.content.rendered, headings);
    
    // Ersetze Bild-URLs im Inhalt durch lokale Pfade
    contentWithIds = await downloadPostImages(post.slug, contentWithIds);

    // Der WordPress-Inhalt ist Fremd-HTML und wird vor dem Rendern bereinigt.
    contentWithIds = sanitizeArticleHtml(contentWithIds);
    
    // Bestimme den Bildpfad
    let imagePath = `${siteConfig.url}/og?title=${encodeURIComponent(post.title.rendered)}`;
    
    // Verwende den lokalen Pfad zum heruntergeladenen Bild in public
    if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0]) {
      const sourceUrl = post._embedded['wp:featuredmedia'][0].source_url;
      // Siehe oben: Endung muss zu der vom Download-Skript geschriebenen passen.
      const fileExt = safeImageExtension(sourceUrl);
      imagePath = `/blog-images/${post.slug}/cover${fileExt}`;
    }
    
    // Verwende die Rank Math Beschreibung, wenn verfügbar, sonst den Excerpt
    let summary = post.meta?.rank_math_description 
      ? post.meta.rank_math_description 
      : stripHtml(post.excerpt.rendered);
    
    // Für die Detailseite können wir eine längere Zusammenfassung verwenden
    summary = truncateText(summary, 250);
      
    return {
      source: contentWithIds,
      metadata: {
        title: post.title.rendered,
        publishedAt: post.date,
        summary: summary,
        author: post._embedded?.author?.[0]?.name ?? "SavagesCorp", // Standardwert, kann später angepasst werden
        image: imagePath
      },
      headings: headings,
      slug: post.slug,
      translations: post.translations
    };
  } catch (error) {
    return null;
  }
}

// Funktion zum Abrufen eines Posts anhand seiner ID
export async function getPostSlugById(id: number): Promise<string | null> {
  try {
    const wordpressUrl = getWordPressUrl(`posts/${id}`);
    const response = await fetch(wordpressUrl);
    
    if (!response.ok) {
      return null;
    }
    
    const post: WordPressPost = await response.json();
    return post.slug;
  } catch (error) {
    return null;
  }
}

// Muss mit ALLOWED_IMAGE_HOSTS in scripts/download-images.js uebereinstimmen.
const ALLOWED_IMAGE_HOSTS = new Set(["savagescorpai.kinsta.cloud"]);

function isAllowedImageUrl(rawUrl: string): boolean {
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === "https:" && ALLOWED_IMAGE_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

function safeImageExtension(rawUrl: string, fallback = ".jpg"): string {
  try {
    const ext = path.extname(new URL(rawUrl).pathname).toLowerCase();
    return /^\.(jpe?g|png|gif|webp|avif|svg)$/.test(ext) ? ext : fallback;
  } catch {
    return fallback;
  }
}

// Hilfsfunktion zum Ersetzen von Bild-URLs im Inhalt
export async function downloadPostImages(slug: string, content: string): Promise<string> {
  // Ersetze alle Bild-URLs im Inhalt durch lokale Pfade
  let modifiedContent = content;
  
  // Extrahiere alle Bild-URLs aus dem Inhalt
  const imgRegex = /<img[^>]+src="([^">]+)"/g;
  let match;
  let imageIndex = 1;
  
  while ((match = imgRegex.exec(content)) !== null) {
    const imageUrl = match[1];

    // Muss mit der Filterlogik in scripts/download-images.js identisch bleiben,
    // sonst passen die hier erzeugten image-N-Pfade nicht zu den heruntergeladenen
    // Dateien. Host-Allowlist statt includes(): "…kinsta.cloud.evil.tld" und
    // "https://evil.tld/?x=savagescorpai.kinsta.cloud" wuerden includes() passieren.
    if (!isAllowedImageUrl(imageUrl)) {
      continue;
    }

    const fileExt = safeImageExtension(imageUrl);
    const localPath = `/blog-images/${slug}/image-${imageIndex}${fileExt}`;
    
    // Ersetze die URL durch den lokalen Pfad
    modifiedContent = modifiedContent.replace(
      new RegExp(imageUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      localPath
    );
    
    imageIndex++;
  }
  
  return modifiedContent;
}
